import { useEffect, useState } from "react";
import api, { formatDate, BACKEND_URL } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import StatusPill from "@/components/StatusPill";
import { toast } from "sonner";
import { Check, X, FileText, Eye } from "lucide-react";

export default function AdminKyc() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [filesFor, setFilesFor] = useState(null); // user_code being viewed
  const [files, setFiles] = useState([]);

  const load = () => {
    const url = status === "needs_docs" ? "/admin/kyc/needs-docs" : `/admin/kyc${status ? `?status=${status}` : ""}`;
    api.get(url).then((r) => setRows(r.data));
  };
  useEffect(() => { load(); }, [status]);

  const decide = async (id, decision) => {
    await api.post(`/admin/kyc/${id}/decision?decision=${decision}`);
    toast.success(`KYC ${decision}d`);
    load();
  };

  const openFiles = async (userCode) => {
    setFilesFor(userCode);
    setFiles([]);
    try {
      const { data } = await api.get(`/admin/kyc/${userCode}/files`);
      // fetch each file as blob with auth header so we can render <img>
      const token = localStorage.getItem("samara_token");
      const withUrls = await Promise.all(data.map(async (f) => {
        try {
          const res = await fetch(`${BACKEND_URL}/api/kyc/file/${f.id}`, { headers: { Authorization: `Bearer ${token}` } });
          const blob = await res.blob();
          return { ...f, blob_url: URL.createObjectURL(blob) };
        } catch { return { ...f, blob_url: null }; }
      }));
      setFiles(withUrls);
    } catch (e) {
      toast.error("Failed to load files");
    }
  };

  const closeFiles = () => {
    files.forEach((f) => f.blob_url && URL.revokeObjectURL(f.blob_url));
    setFilesFor(null); setFiles([]);
  };

  return (
    <DashboardLayout title="KYC Approval" subtitle={`${rows.length} pending/processed`}>
      <div className="mb-4 flex gap-2">
        {[{ k: "pending", l: "Pending" }, { k: "approved", l: "Approved" }, { k: "rejected", l: "Rejected" }, { k: "", l: "All" }].map((s) => (
          <button key={s.k} data-testid={`kyc-tab-${s.k || "all"}`} onClick={() => setStatus(s.k)} className={`px-3 py-1.5 text-xs border rounded-sm ${status === s.k ? "bg-navy text-gold border-navy" : "border-border text-navy hover:border-gold"}`}>{s.l}</button>
        ))}
      </div>
      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-navy text-white">
            <tr>{["User", "PAN", "Aadhar", "Bank", "Account", "Status", "Date", "Docs", ""].map((h) => <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-navy/50">No KYC records</td></tr>}
            {rows.map((k) => (
              <tr key={k.id} data-testid={`kyc-row-${k.id}`} className="border-b border-border last:border-0 hover:bg-cream/40">
                <td className="px-4 py-3 text-navy font-medium">{k.user_code}</td>
                <td className="px-4 py-3 text-navy/70">{k.pan}</td>
                <td className="px-4 py-3 text-navy/70">{k.aadhar}</td>
                <td className="px-4 py-3 text-navy/60">{k.bank_name}</td>
                <td className="px-4 py-3 text-navy/60">{k.bank_account}</td>
                <td className="px-4 py-3"><StatusPill status={k.status} /></td>
                <td className="px-4 py-3 text-navy/50 text-xs">{formatDate(k.created_at)}</td>
                <td className="px-4 py-3">
                  <button data-testid={`view-docs-${k.user_code}`} onClick={() => openFiles(k.user_code)} className="text-xs text-navy hover:text-gold inline-flex items-center gap-1">
                    <Eye size={12} /> View
                  </button>
                </td>
                <td className="px-4 py-3">
                  {k.status === "pending" && (
                    <div className="flex gap-1">
                      <button data-testid={`kyc-approve-${k.id}`} onClick={() => decide(k.id, "approve")} className="p-1.5 bg-olive/10 text-olive hover:bg-olive/20 rounded-sm"><Check size={14} /></button>
                      <button data-testid={`kyc-reject-${k.id}`} onClick={() => decide(k.id, "reject")} className="p-1.5 bg-rose/10 text-rose hover:bg-rose/20 rounded-sm"><X size={14} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filesFor && (
        <div data-testid="kyc-files-modal" className="fixed inset-0 bg-navy/70 backdrop-blur-sm z-50 grid place-items-center p-4">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] rounded-sm overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-gold">Documents</div>
                <h3 className="font-serif text-2xl text-navy">{filesFor}</h3>
              </div>
              <button data-testid="kyc-files-close" onClick={closeFiles} className="text-navy/60 hover:text-rose p-2"><X size={18} /></button>
            </div>
            <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-5">
              {files.length === 0 && <div className="col-span-2 text-center text-navy/50 py-10">No documents uploaded by this user.</div>}
              {files.map((f) => {
                const isImg = (f.content_type || "").startsWith("image/");
                return (
                  <div key={f.id} className="border border-border rounded-sm overflow-hidden bg-cream">
                    <div className="px-4 py-3 bg-white border-b border-border flex items-center gap-2">
                      <FileText size={14} className="text-gold" />
                      <span className="text-xs uppercase tracking-widest text-navy/60 capitalize">{f.doc_type}</span>
                      <a data-testid={`download-doc-${f.id}`} href={f.blob_url} target="_blank" rel="noreferrer" download={f.original_filename || `${f.doc_type}.bin`} className="ml-auto text-xs text-gold hover:underline">Open ↗</a>
                    </div>
                    <div className="aspect-video bg-navy/5 grid place-items-center overflow-hidden">
                      {isImg && f.blob_url ? (
                        <img data-testid={`thumb-${f.id}`} src={f.blob_url} alt={f.doc_type} className="w-full h-full object-contain" />
                      ) : (
                        <div className="text-center text-navy/50 text-xs">
                          <FileText size={40} className="mx-auto text-navy/30 mb-2" />
                          {f.original_filename}<br /><span className="text-[10px]">{f.content_type}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
